/**
 * useAssistant — hook para el chat con Deenex.
 *
 * Maneja:
 *  - Carga del historial al montar
 *  - Send con typing indicator (delay 600-1200ms para que se sienta humano)
 *  - Clear conversation
 *  - Optimistic append del mensaje del user antes de la respuesta del server
 *
 * `enabled = false` no carga el historial: el assistant es de la plataforma anterior y
 * /assistant/history no existe en el backend de Activa.
 */

import { useCallback, useEffect, useState } from "react";
import {
    assistantApi,
    type AssistantMessage,
} from "../services/api/assistant.api";

export function useAssistant(enabled = true) {
    const [messages, setMessages] = useState<AssistantMessage[]>([]);
    const [loading, setLoading] = useState(enabled);
    const [typing, setTyping] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /* Cargar historial al montar */
    useEffect(() => {
        if (!enabled) {
            setLoading(false);
            return;
        }
        (async () => {
            const r = await assistantApi.history();
            if (r.success && r.data) setMessages(r.data);
            setLoading(false);
        })();
    }, [enabled]);

    const send = useCallback(async (text: string) => {
        const trimmed = text.trim();
        if (!trimmed || typing) return;
        setError(null);

        // Optimistic: append user message instantáneo
        const tempUser: AssistantMessage = {
            id: "tmp-" + Date.now(),
            role: "user",
            content: trimmed,
            createdAt: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, tempUser]);
        setTyping(true);

        // Delay artificial para que se sienta como que la IA piensa
        const thinkMs = 800 + Math.random() * 600;
        await new Promise((res) => setTimeout(res, thinkMs));

        const r = await assistantApi.chat(trimmed);
        setTyping(false);

        if (r.success && r.data) {
            // Reemplazar el optimistic + agregar la respuesta
            setMessages((prev) => [
                ...prev.filter((m) => m.id !== tempUser.id),
                r.data!.userMessage,
                r.data!.assistantMessage,
            ]);
        } else {
            // Rollback + mostrar error
            setMessages((prev) => prev.filter((m) => m.id !== tempUser.id));
            setError(r.error || "No pude responder, probá de nuevo");
        }
    }, [typing]);

    const clear = useCallback(async () => {
        setMessages([]);
        await assistantApi.clear();
    }, []);

    return { messages, loading, typing, error, send, clear };
}
